import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_PAGE_SIZE } from "../../../configs/apiConfig";
import { FilterType } from "../../../configs/filterTypes";
import { ColumnAttribute } from "../../../configs/productOrder";
import { SortType } from "../../../configs/sortTypes";
import Spinner from "../../../shared/components/LoadingPage";
import PaginationBar from "../../../shared/components/PaginationBar";
import SearchFilterBar, { ButtonType } from "../../../shared/components/SearchFilterBar";
import { api } from "../../hooks/api";
import { useTabURLSync } from "../../hooks/useTabURLSync";
import { ContentWrapper, ExpandableContent } from "../../styled/TableStyles";
import CustomTable from "../CustomTable";

interface ListViewProps {
    url: string;
    defaultPageSize?: number;
    processItem?: Function;
    sortTypes?: SortType[];
    filterTypes?: FilterType[];
    rowElement: any;
    tableColumns: ColumnAttribute[];
    textString?: string;
    refresh?: boolean;
    selectable?: boolean;
    rightButtons?: ButtonType[];
    onSelectionChange?: (selectedItems: any[]) => void;
    enableUrlRefresh?: boolean;
    skipEmptyUrlRefresh?: boolean;
}

const ListView = ({
                      url,
                      defaultPageSize,
                      processItem,
                      sortTypes,
                      filterTypes,
                      rowElement,
                      tableColumns,
                      textString,
                      refresh,
                      selectable,
                      rightButtons,
                      onSelectionChange,
                      enableUrlRefresh,
                      skipEmptyUrlRefresh,
                  }: ListViewProps) => {
    const { updateCurrentTabParams, getCurrentTabParams } = useTabURLSync();
    const location = useLocation();
    const navigate = useNavigate();
    const expandableContentRef = useRef<HTMLDivElement>(null);
    const lastUrlFetchRef = useRef<string>("__unset__");
    const lastCanonicalFetchRef = useRef<string>("__unset__");
    const canonicalizeSearch = useCallback(
        (raw: string, page: number, pageSize: number) => {
            const params = new URLSearchParams(raw);
            if (!params.get("page")) {
                params.set("page", String(page));
            }
            if (!params.get("page_size")) {
                params.set("page_size", String(pageSize));
            }
            const entries = Array.from(params.entries()).sort(([a], [b]) =>
                a.localeCompare(b)
            );
            const normalized = new URLSearchParams(entries).toString();
            return normalized ? `?${normalized}` : "";
        },
        []
    );
    const lastRefreshRef = useRef<boolean | undefined>(refresh);

    const searchParams = useMemo(() => {
        return new URLSearchParams(location.search);
    }, [location.search]);

    const searchValue = searchParams.get("search");
    const scrollPositionRef = useRef<number>(0);

    // Read all URL parameters for initial API call, excluding tab and empty values
    const urlParams: Record<string, string> = useMemo(() => {
        const a: Record<string, string> = {};
        searchParams.forEach((value, key) => {
            // Exclude tab parameter and empty values from API call
            if (key !== "tab" && key !== "page" && key !== "page_size" && value !== "" && value !== null && value !== undefined) {
                a[key] = value;
            }
        });
        return a;
    }, [searchParams]);


    // Get page and page_size from URL or use defaults
    const pageFromURL = searchParams.get("page");
    const pageSizeFromURL = searchParams.get("page_size");

    const getDefaultFilterParams = (filterTypes?: FilterType[]) => {
        const defaultParams: Record<string, string> = {};
        const queryParams = new URLSearchParams(location.search);
        if (filterTypes) {
            filterTypes.forEach((filter) => {
                if (!queryParams.has(filter.field)) {
                    const defaultOptions = filter.options.filter(option => option.isDefault);
                    if (defaultOptions.length > 0) {
                        // Join multiple default options with comma
                        defaultParams[filter.field] = defaultOptions.map(opt => opt.value).join(",");
                    }
                }
            });
        }
        return defaultParams;
    }

    const initialParams = {
        page_size: pageSizeFromURL ? parseInt(pageSizeFromURL) : (defaultPageSize || DEFAULT_PAGE_SIZE),
        page: pageFromURL ? parseInt(pageFromURL) : 1,
        ...(filterTypes && filterTypes.length > 0 ? getDefaultFilterParams(filterTypes) : {}),
        ...(searchValue && searchValue.trim() !== "" && { search: searchValue }),
        ...urlParams, // Include all other URL parameters (excluding tab)
    };

    const [{ data, variables, isLoading }, fetchItems] = api.get(
        url,
        initialParams,
        enableUrlRefresh
            ? true
            : Boolean(sortTypes || filterTypes) && !searchValue, // Only disable if search/filter needed AND no search param
    );

    useEffect(() => {
        if (refresh === undefined) return;
        if (lastRefreshRef.current === refresh) return;
        if (!isLoading) {
            lastRefreshRef.current = refresh;
            fetchItems();
        }
    }, [refresh, isLoading]);

    const [currentPage, setCurrentPage] = useState(initialParams.page);

    const rawItems = data?.results || [];
    const processedItems = processItem ? rawItems.map(processItem) : rawItems;
    const totalItems = data?.count || 0;
    const pageSize = variables?.page_size || initialParams.page_size;

    // Function to sync current state with URL parameters
    const syncURLWithState = useCallback(() => {
        const currentState = {
            page: currentPage.toString(),
            page_size: pageSize.toString(),
            ...(filterTypes && filterTypes.length > 0 ? getDefaultFilterParams(filterTypes) : {}),
            ...(searchValue && { search: searchValue }),
            ...urlParams,
        };
        updateCurrentTabParams(currentState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, searchValue, urlParams, updateCurrentTabParams]);

    // Function to clear all filters (like Xóa bộ lọc)
    const clearAllFilters = () => {
        // Reset current page to 1
        setCurrentPage(1);

        // Preserve tab parameter when resetting filters (same as FilterOptions)
        const currentTab = new URLSearchParams(location.search).get("tab");
        if (currentTab) {
            navigate(`${location.pathname}?tab=${currentTab}`);
        } else {
            navigate(location.pathname);
        }

        // Fetch fresh data with reset parameters (same as FilterOptions onClickResetFilters)
        fetchItems({
            page_size: DEFAULT_PAGE_SIZE,
            page: 1,
        }, true);
    };

    // Function to refresh data with current filters
    const refreshData = () => {
        // Store the current scroll position
        const elementScrollTop = expandableContentRef.current?.scrollTop || 0;
        const elementScrollHeight = expandableContentRef.current?.scrollHeight || 0;
        const elementClientHeight = expandableContentRef.current?.clientHeight || 0;

        // Store scroll position in ref for persistence
        scrollPositionRef.current = elementScrollTop;

        // Refetch data with current parameters
        fetchItems();

        // Enhanced scroll restoration with requestAnimationFrame for better timing
        const restoreScroll = () => {
            if (expandableContentRef.current && scrollPositionRef.current > 0) {
                const currentScrollHeight = expandableContentRef.current.scrollHeight;
                const currentClientHeight = expandableContentRef.current.clientHeight;

                // Only restore if the content has actually changed
                if (currentScrollHeight !== elementScrollHeight || currentClientHeight !== elementClientHeight) {
                    requestAnimationFrame(() => {
                        if (expandableContentRef.current) {
                            expandableContentRef.current.scrollTop = scrollPositionRef.current;
                        }
                    });
                }
            }
        };

        // Try multiple times with increasing delays to ensure data is loaded
        setTimeout(restoreScroll, 50);
        setTimeout(restoreScroll, 150);
        setTimeout(restoreScroll, 300);
        setTimeout(restoreScroll, 500);
        setTimeout(restoreScroll, 800);
    };

    const buildParamsFromUrl = useCallback(() => {
        const params = new URLSearchParams(location.search);
        const pageFromURL = params.get("page");
        const pageSizeFromURL = params.get("page_size");
        const searchValueFromURL = params.get("search");

        const urlParams: Record<string, string> = {};
        params.forEach((value, key) => {
            if (
                key !== "tab" &&
                key !== "page" &&
                key !== "page_size" &&
                value !== "" &&
                value !== null &&
                value !== undefined
            ) {
                urlParams[key] = value;
            }
        });

        return {
            page_size: pageSizeFromURL
                ? parseInt(pageSizeFromURL)
                : defaultPageSize || DEFAULT_PAGE_SIZE,
            page: pageFromURL ? parseInt(pageFromURL) : 1,
            ...(filterTypes && filterTypes.length > 0
                ? getDefaultFilterParams(filterTypes)
                : {}),
            ...(searchValueFromURL &&
                searchValueFromURL.trim() !== "" && { search: searchValueFromURL }),
            ...urlParams,
        };
    }, [location.search, defaultPageSize, filterTypes]);

    useEffect(() => {
        if (!enableUrlRefresh) return;
        const current = location.search || "";
        if (skipEmptyUrlRefresh && current === "") {
            return;
        }
        const params = buildParamsFromUrl();
        const canonical = canonicalizeSearch(
            current,
            Number(params.page || 1),
            Number(params.page_size || DEFAULT_PAGE_SIZE)
        );
        if (lastCanonicalFetchRef.current === canonical) return;
        lastCanonicalFetchRef.current = canonical;
        lastUrlFetchRef.current = current;
        fetchItems(buildParamsFromUrl(), true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildParamsFromUrl, canonicalizeSearch, enableUrlRefresh, location.search, skipEmptyUrlRefresh]);

    // Auto-sync URL parameters when data loads or state changes
    useEffect(() => {
        if (!isLoading && data) {
            // Small delay to ensure all state is updated
            const timer = setTimeout(() => {
                if (enableUrlRefresh) {
                    const current = location.search || "";
                    lastUrlFetchRef.current = current;
                }
                syncURLWithState();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [data, currentPage, pageSize, searchValue, isLoading, syncURLWithState]);

    // Restore scroll position when data loads
    useEffect(() => {
        if (!isLoading && data && scrollPositionRef.current > 0) {
            const timer = setTimeout(() => {
                if (expandableContentRef.current) {
                    expandableContentRef.current.scrollTop = scrollPositionRef.current;
                }
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [data, isLoading]);

    // Track scroll position continuously
    useEffect(() => {
        const handleScroll = () => {
            if (expandableContentRef.current) {
                scrollPositionRef.current = expandableContentRef.current.scrollTop;
            }
        };

        const element = expandableContentRef.current;
        if (element) {
            element.addEventListener("scroll", handleScroll);
            return () => element.removeEventListener("scroll", handleScroll);
        }
    }, []);

    // Cleanup function to clear old tab parameters (optional)
    useEffect(() => {
        return () => {
            // This could be used to clean up old tab parameters if needed
            // For now, we keep them in sessionStorage for the session
        };
    }, []);

    // Update current page when URL changes
    useEffect(() => {
        if (pageFromURL) {
            setCurrentPage(parseInt(pageFromURL));
        }
    }, [pageFromURL]);

    return (
        <ContentWrapper>
            <ExpandableContent ref={expandableContentRef} data-testid="expandable-content">
                <SearchFilterBar
                    textString={textString}
                    sortTypes={sortTypes}
                    fetchItems={fetchItems}
                    filterTypes={filterTypes}
                    setCurrentPage={setCurrentPage}
                    onRefresh={refreshData}
                    onClearFilters={clearAllFilters}
                    rightButtons={rightButtons}
                />
                {isLoading ? (
                    <Spinner />
                ) : (
                    <>
                        <CustomTable
                            itemList={processedItems}
                            RowElement={rowElement}
                            TableColumns={tableColumns}
                            extraRowProps={{ fetchItems }}
                            selectable={selectable}
                            onSelectionChange={onSelectionChange}
                        />
                        {/* Show pagination inside when there are few items */}
                        {processedItems.length <= 15 && (
                            <PaginationBar
                                totalItems={totalItems}
                                pageSize={pageSize}
                                fetchItems={fetchItems}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                            />
                        )}
                    </>
                )}
            </ExpandableContent>
            {/* Show pagination outside when there are many items */}
            {processedItems.length > 15 && (
                <PaginationBar
                    totalItems={totalItems}
                    pageSize={pageSize}
                    fetchItems={fetchItems}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                />
            )}
        </ContentWrapper>
    );
};

export default ListView;
